'use client'

import { PayoutScheduleDisplay } from '@/components/payout-schedule/PayoutScheduleDisplay'
import { getSupplier, Supplier } from '@/lib/api/suppliers/supplier-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBusiness,
    MdCalendarToday,
    MdContactPhone,
    MdEdit,
    MdEmail,
    MdLanguage,
    MdLocalShipping,
    MdLocationOn,
    MdNotes,
    MdPhone,
    MdToggleOff,
    MdToggleOn,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import NotificationPreferencesCard from '@/components/notification-preferences/notification-preferences-card'

const formatCnpj = (cnpj: string): string => {
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
    )
}

export default function SupplierDetailPage() {
    const navigate = useNavigate()
    const { canUpdate } = useResourcePermissions('person', 'suppliers')
    const { id } = useParams<{ id: string }>()
    const [supplier, setSupplier] = useState<Supplier | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (id) {
            loadSupplier()
        }
    }, [id])

    const loadSupplier = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getSupplier(parseInt(id, 10))
            if (response.success && response.result) {
                setSupplier(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar fornecedor',
                )
                navigate('/person/suppliers')
            }
        } catch {
            toast.error('Erro ao carregar fornecedor')
            navigate('/person/suppliers')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do fornecedor...
                </p>
            </div>
        )
    }

    if (!supplier) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdLocalShipping className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Fornecedor não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O fornecedor que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/person/suppliers')}
                    className="btn btn-secondary"
                >
                    Voltar para Fornecedores
                </button>
            </div>
        )
    }

    const fullAddress = [
        supplier.street,
        supplier.number,
        supplier.complement,
        supplier.neighborhood,
        supplier.city,
        supplier.state,
        supplier.zipCode,
    ]
        .filter(Boolean)
        .join(', ')

    const hasContactInfo =
        supplier.contactName || supplier.contactPhone || supplier.contactEmail

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/person/suppliers')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 items-center justify-center shadow-lg">
                            <MdLocalShipping className="text-2xl text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                    {supplier.name}
                                </h1>
                                <span
                                    className={`badge badge-sm ${
                                        supplier.active
                                            ? 'badge-success'
                                            : 'badge-error'
                                    }`}
                                >
                                    {supplier.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                            {supplier.tradingName && (
                                <p className="text-sm text-base-content/60">
                                    {supplier.tradingName}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <button
                        onClick={() =>
                            navigate(`/person/suppliers/edit/${supplier.id}`)
                        }
                        className="btn btn-secondary gap-2"
                    >
                        <MdEdit className="text-lg" />
                        <span className="hidden sm:inline">Editar</span>
                    </button>
                )}
            </div>

            {/* Datas */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                Criado em{' '}
                                {format(
                                    new Date(supplier.createdAt),
                                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR },
                                )}
                            </span>
                        </div>
                        {supplier.updatedAt !== supplier.createdAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <span>
                                    Atualizado em{' '}
                                    {format(
                                        new Date(supplier.updatedAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR },
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {/* Dados Gerais */}
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <MdBusiness className="text-secondary" />
                        Dados Gerais
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        {supplier.cnpj && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdBusiness className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        CNPJ
                                    </p>
                                    <p className="text-sm font-semibold text-base-content font-mono">
                                        {formatCnpj(supplier.cnpj)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {supplier.stateRegistration && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdBusiness className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Inscrição Estadual
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {supplier.stateRegistration}
                                    </p>
                                </div>
                            </div>
                        )}

                        {supplier.email && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdEmail className="text-xl text-info" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Email
                                    </p>
                                    <a
                                        href={`mailto:${supplier.email}`}
                                        className="text-sm font-semibold text-info hover:underline break-all"
                                    >
                                        {supplier.email}
                                    </a>
                                </div>
                            </div>
                        )}

                        {supplier.phone && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-success/10 rounded-lg">
                                    <MdPhone className="text-xl text-success" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Telefone
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {supplier.phone}
                                    </p>
                                </div>
                            </div>
                        )}

                        {supplier.website && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdLanguage className="text-xl text-accent" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Website
                                    </p>
                                    <a
                                        href={
                                            supplier.website.startsWith('http')
                                                ? supplier.website
                                                : `https://${supplier.website}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-semibold text-accent hover:underline break-all"
                                    >
                                        {supplier.website}
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                            <div className="p-2 bg-base-300/50 rounded-lg">
                                {supplier.active ? (
                                    <MdToggleOn className="text-xl text-success" />
                                ) : (
                                    <MdToggleOff className="text-xl text-error" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Status
                                </p>
                                <p className="text-sm font-semibold text-base-content">
                                    {supplier.active ? 'Ativo' : 'Inativo'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Endereço */}
                    {fullAddress && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                                <MdLocationOn className="text-warning" />
                                Endereço
                            </h3>
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl mb-6">
                                <div className="p-2 bg-warning/10 rounded-lg">
                                    <MdLocationOn className="text-xl text-warning" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-base-content">
                                        {fullAddress}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Contato */}
                    {hasContactInfo && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                                <MdContactPhone className="text-secondary" />
                                Pessoa de Contato
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                {supplier.contactName && (
                                    <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                        <div className="p-2 bg-secondary/10 rounded-lg">
                                            <MdContactPhone className="text-xl text-secondary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Nome
                                            </p>
                                            <p className="text-sm font-semibold text-base-content">
                                                {supplier.contactName}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {supplier.contactPhone && (
                                    <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                        <div className="p-2 bg-success/10 rounded-lg">
                                            <MdPhone className="text-xl text-success" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Telefone
                                            </p>
                                            <p className="text-sm font-semibold text-base-content">
                                                {supplier.contactPhone}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {supplier.contactEmail && (
                                    <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                        <div className="p-2 bg-info/10 rounded-lg">
                                            <MdEmail className="text-xl text-info" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Email
                                            </p>
                                            <a
                                                href={`mailto:${supplier.contactEmail}`}
                                                className="text-sm font-semibold text-info hover:underline break-all"
                                            >
                                                {supplier.contactEmail}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Repasse de Valores */}
                    <div className="mb-6">
                        <PayoutScheduleDisplay
                            payoutScheduleType={supplier.payoutScheduleType}
                            payoutFixedDay={supplier.payoutFixedDay}
                            payoutDaysAfterAppointment={supplier.payoutDaysAfterAppointment}
                            title="Repasse de Valores ao Fornecedor"
                        />
                    </div>


                    {/* Notificações: por quais canais este fornecedor aceita ser avisado.
                        Fica no detalhe, e não na edição, porque é decisão de
                        relacionamento — e vale na hora, sem salvar a tela inteira. */}
                    <NotificationPreferencesCard
                        entityType="supplier"
                        entityId={supplier.id}
                        preferences={supplier.notificationPreferences}
                        entityName={supplier.name}
                        canEdit={canUpdate}
                    />

                    {/* Observações */}
                    {supplier.notes && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                                <MdNotes className="text-secondary" />
                                Observações
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-sm text-base-content whitespace-pre-wrap">
                                    {supplier.notes}
                                </p>
                            </div>
                        </>
                    )}

                    {/* Sem informações */}
                    {!supplier.cnpj &&
                        !supplier.email &&
                        !supplier.phone &&
                        !supplier.website &&
                        !fullAddress &&
                        !hasContactInfo &&
                        !supplier.notes && (
                            <div className="text-center py-8 text-base-content/60">
                                <p>
                                    Nenhuma informação adicional disponível
                                </p>
                            </div>
                        )}
                </div>
            </div>
        </div>
    )
}
